import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getRoot,
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  createCommand,
  type LexicalCommand,
  type LexicalNode,
} from "lexical";
import { useEffect } from "react";

import { $createMergeTagNode, $isMergeTagNode } from "./MergeTagNode";
import {
  $ancestorProductBlock,
  $ancestorProductContext,
  $insertProductBlockInContext,
  $insertProductContextAtCaret,
  $productContextBase,
} from "./ProductBlockPlugin";
import { useDefaultProductContextModel } from "./useDefaultProductContextModel";
import { getMergeTagByIdentifier } from "../mergeTagDefinitions";
import { DISCOVER_HEADING_TAG } from "../productContexts";
import {
  parseProductItemIdentifier,
  productItemIdentifier,
} from "../productItemTags";

import { mergeTagsService, type ResolvedFilters } from "@/services/mergeTags";

export interface InsertMergeTagPayload {
  /** The raw identifier, e.g. `customer.first_name`. */
  tag: string;
  /** Human-readable label, e.g. "First Name". */
  label: string;
  /** Initial filters for the node (e.g. from the tag's defaultFilters). */
  filters?: ResolvedFilters;
}

export const INSERT_MERGE_TAG_COMMAND: LexicalCommand<InsertMergeTagPayload> =
  createCommand("INSERT_MERGE_TAG_COMMAND");

export function MergeTagPlugin(): null {
  const [editor] = useLexicalComposerContext();
  const defaultContextModel = useDefaultProductContextModel();

  useEffect(() => {
    return editor.registerCommand<InsertMergeTagPayload>(
      INSERT_MERGE_TAG_COMMAND,
      (payload) => {
        if (
          !mergeTagsService.find(payload.tag) &&
          !getMergeTagByIdentifier(payload.tag)
        ) {
          return false;
        }

        const selection = $getSelection();

        // The Discover headline is bound by a Discover section's open marker;
        // anywhere else it would render empty.
        if (payload.tag === DISCOVER_HEADING_TAG) {
          const anchorNode = $isRangeSelection(selection)
            ? selection.anchor.getNode()
            : null;
          if ($ancestorProductContext(anchorNode)?.getModel() !== "discover") {
            return true;
          }
        }

        // Product tags always land in a context: inside a block the chip reads
        // the block's product; inside context copy it reads the context's
        // first item; anywhere else a fresh context wraps it.
        const item = parseProductItemIdentifier(payload.tag);
        if (item) {
          const anchorNode = $isRangeSelection(selection)
            ? selection.anchor.getNode()
            : null;
          const block = $ancestorProductBlock(anchorNode);
          const context = block ? null : $ancestorProductContext(anchorNode);
          const index = block
            ? block.getIndex()
            : context
              ? $productContextBase(context)
              : 0;
          const identifier = productItemIdentifier(index, item.field);
          const entry = getMergeTagByIdentifier(identifier);
          if (!entry) return false;
          const chip = $createMergeTagNode(
            entry.identifier,
            entry.label,
            payload.filters,
          );
          if (block) {
            if ($isRangeSelection(selection)) selection.insertNodes([chip]);
            return true;
          }
          if (context) {
            // Inside a context but not a block: wrap the chip in its own guarded
            // block, mirroring the fresh-context path — never a loose chip.
            let line: LexicalNode | null = anchorNode;
            while (line !== null && !context.is(line.getParent())) {
              line = line.getParent();
            }
            $insertProductBlockInContext(context, line, chip);
            return true;
          }
          $insertProductContextAtCaret(defaultContextModel, chip);
          return true;
        }

        const node = $createMergeTagNode(
          payload.tag,
          payload.label,
          payload.filters,
        );

        if ($isRangeSelection(selection)) {
          selection.insertNodes([node]);
        } else {
          const root = $getRoot();
          const lastChild = root.getLastChild();
          if ($isElementNode(lastChild)) {
            lastChild.append(node);
          } else {
            root.append(node);
          }
        }
        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor, defaultContextModel]);

  useEffect(() => {
    return editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      (event) => {
        const selection = $getSelection();

        if ($isNodeSelection(selection)) {
          const nodes = selection.getNodes();
          let handled = false;
          for (const node of nodes) {
            if ($isMergeTagNode(node)) {
              node.remove();
              handled = true;
            }
          }
          if (handled) {
            event?.preventDefault();
            return true;
          }
        }

        if ($isRangeSelection(selection) && selection.isCollapsed()) {
          const { anchor } = selection;
          const anchorNode = anchor.getNode();
          const anchorOffset = anchor.offset;

          if ($isElementNode(anchorNode)) {
            const childAtOffset = anchorNode.getChildAtIndex(anchorOffset - 1);
            if ($isMergeTagNode(childAtOffset)) {
              childAtOffset.remove();
              event?.preventDefault();
              return true;
            }
          }

          if (anchorOffset === 0) {
            const prevSibling = anchorNode.getPreviousSibling();
            if ($isMergeTagNode(prevSibling)) {
              prevSibling.remove();
              event?.preventDefault();
              return true;
            }
          }

          if ($isMergeTagNode(anchorNode)) {
            anchorNode.remove();
            event?.preventDefault();
            return true;
          }
        }

        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      KEY_DELETE_COMMAND,
      (event) => {
        const selection = $getSelection();

        if ($isNodeSelection(selection)) {
          const nodes = selection.getNodes();
          let handled = false;
          for (const node of nodes) {
            if ($isMergeTagNode(node)) {
              node.remove();
              handled = true;
            }
          }
          if (handled) {
            event?.preventDefault();
            return true;
          }
        }

        if ($isRangeSelection(selection) && selection.isCollapsed()) {
          const { anchor } = selection;
          const anchorNode = anchor.getNode();
          const anchorOffset = anchor.offset;

          if ($isElementNode(anchorNode)) {
            const childAtOffset = anchorNode.getChildAtIndex(anchorOffset);
            if ($isMergeTagNode(childAtOffset)) {
              childAtOffset.remove();
              event?.preventDefault();
              return true;
            }
          }

          const textContentLength = anchorNode.getTextContentSize();
          if (anchorOffset === textContentLength) {
            const nextSibling = anchorNode.getNextSibling();
            if ($isMergeTagNode(nextSibling)) {
              nextSibling.remove();
              event?.preventDefault();
              return true;
            }
          }

          if ($isMergeTagNode(anchorNode)) {
            anchorNode.remove();
            event?.preventDefault();
            return true;
          }
        }

        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor]);

  return null;
}
